import React, { useState, useEffect, useRef } from 'react';
import { searchAddress, AddressSuggestion } from '../../utils/addressAutocomplete';
import { Address, Coordinates } from '../../types';
import { MapPin, Lightbulb } from 'lucide-react';
import './AddressAutocomplete.css';

interface AddressAutocompleteProps {
  value: string;
  onChange: (
    location: string,
    address?: Address,
    coordinates?: Coordinates,
    placeLabel?: string,
  ) => void;
  placeholder?: string;
  className?: string;
  /** `places` includes businesses; `address` is street-focused. */
  searchMode?: 'address' | 'places';
  /** Fired on each keystroke so parents can read the in-progress query. */
  onInputChange?: (value: string) => void;
}

const AddressAutocomplete: React.FC<AddressAutocompleteProps> = ({
  value,
  onChange,
  onInputChange,
  placeholder = 'Enter address or location...',
  className = '',
  searchMode = 'address',
}) => {
  const [inputValue, setInputValue] = useState(value);
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Update input value when prop changes
  useEffect(() => {
    setInputValue(value);
  }, [value]);

  // Handle input changes with debouncing
  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    const trimmed = inputValue.trim();
    // Allow single character for state abbreviations, but require at least 1 character
    if (trimmed.length < 1) {
      setSuggestions([]);
      setShowSuggestions(false);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    debounceTimerRef.current = setTimeout(async () => {
      try {
        const results = await searchAddress(trimmed, 10, searchMode);
        console.log(`Address search for "${trimmed}": Found ${results.length} results`, results);
        setSuggestions(results);
        setShowSuggestions(results.length > 0);
        setIsLoading(false);
        setSelectedIndex(-1);
      } catch (error) {
        console.error('Error searching addresses:', error);
        setSuggestions([]);
        setShowSuggestions(false);
        setIsLoading(false);
      }
    }, 500); // Debounce for API calls

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [inputValue, searchMode]);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    onInputChange?.(e.target.value);
    setShowSuggestions(true);
  };

  const handleInputFocus = () => {
    if (suggestions.length > 0) {
      setShowSuggestions(true);
    }
  };

  const handleSuggestionSelect = (suggestion: AddressSuggestion) => {
    // Build formatted address from suggestion data
    const addr = suggestion.address || {};
    const street = addr.street || addr.road || '';
    const city = addr.city || addr.town || addr.village || '';
    const state = addr.state || addr.region || '';
    const zipCode = addr.zipCode || addr.postcode || '';
    const country = addr.country || 'United States';

    // Create formatted display name
    const addressParts = [street, city, state, zipCode].filter(part => part);
    const formatted = addressParts.length > 0 ? addressParts.join(', ') : suggestion.display_name;

    const address: Address | undefined = street || city ? {
      street: street,
      city: city,
      state: state,
      zipCode: zipCode,
      country: country,
    } : undefined;

    const coordinates: Coordinates = {
      lat: parseFloat(suggestion.lat),
      lng: parseFloat(suggestion.lon),
    };

    console.log('Selected address:', { formatted, address, coordinates });
    setInputValue(formatted);
    setShowSuggestions(false);
    onChange(
      formatted,
      address,
      coordinates,
      suggestion.main_text || suggestion.display_name.split(',')[0]?.trim(),
    );
  };
  
  // Allow manual entry if user presses Enter without selecting
  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // If Enter is pressed, accept the input (either selected suggestion or manual entry)
    if (e.key === 'Enter') {
      e.preventDefault();
      
      // If there's a selected suggestion, use it
      if (showSuggestions && suggestions.length > 0 && selectedIndex >= 0 && selectedIndex < suggestions.length) {
        handleSuggestionSelect(suggestions[selectedIndex]);
        return;
      }
      
      // If there's a suggestion but none selected, use the first one
      if (showSuggestions && suggestions.length > 0) {
        handleSuggestionSelect(suggestions[0]);
        return;
      }
      
      // Otherwise, accept manual entry
      if (inputValue.trim()) {
        const manualAddress = inputValue.trim();
        console.log('Accepting manual address entry (no suggestions found):', manualAddress);
        // Trigger onChange with manual address (geocoding will happen in parent)
        onChange(manualAddress, undefined, undefined);
        setShowSuggestions(false);
        // Clear input focus to close edit mode
        if (inputRef.current) {
          inputRef.current.blur();
        }
      }
      return;
    }
    
    // Handle other keys (arrow navigation, etc.)
    handleKeyDown(e);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showSuggestions || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
          handleSuggestionSelect(suggestions[selectedIndex]);
        } else if (suggestions.length > 0) {
          handleSuggestionSelect(suggestions[0]);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        break;
    }
  };

  const handleInputBlur = () => {
    // Delay to allow suggestion click to register
    setTimeout(() => {
      setShowSuggestions(false);
    }, 200);
  };

  return (
    <div className={`address-autocomplete ${className}`} ref={containerRef}>
      <div className="autocomplete-input-wrapper">
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          onKeyDown={handleInputKeyDown}
          placeholder={placeholder}
          className="autocomplete-input"
        />
        {isLoading && (
          <div className="autocomplete-loading">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" opacity="0.25"></circle>
              <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" opacity="0.5"></path>
            </svg>
          </div>
        )}
      </div>

      {showSuggestions && suggestions.length > 0 && (
        <div className="autocomplete-suggestions">
          {suggestions.map((suggestion, index) => (
            <div
              key={suggestion.place_id}
              className={`suggestion-item ${index === selectedIndex ? 'selected' : ''}`}
              onClick={() => handleSuggestionSelect(suggestion)}
              onMouseEnter={() => setSelectedIndex(index)}
            >
              <div className="suggestion-icon"><MapPin size={16} /></div>
              <div className="suggestion-text">
                <div className="suggestion-main">
                  {suggestion.main_text || (() => {
                    // Format address more nicely
                    const addr = suggestion.address;
                    if (addr?.house_number && addr?.road) {
                      // Full street address
                      return `${addr.house_number} ${addr.road}, ${addr.city || addr.town || addr.village || ''}, ${addr.state || ''} ${addr.postcode || ''}`.trim();
                    } else if (addr?.road) {
                      // Street name only
                      return `${addr.road}, ${addr.city || addr.town || addr.village || ''}, ${addr.state || ''}`.trim();
                    } else if (addr?.city || addr?.town || addr?.village) {
                      // City-level
                      return `${addr.city || addr.town || addr.village}, ${addr.state || ''} ${addr.postcode || ''}`.trim();
                    }
                    return suggestion.display_name;
                  })()}
                </div>
                {(suggestion.secondary_text || suggestion.address) && (
                  <div className="suggestion-details">
                    {suggestion.secondary_text ||
                      [
                        suggestion.address?.city ||
                          suggestion.address?.town ||
                          suggestion.address?.village ||
                          suggestion.address?.municipality ||
                          suggestion.address?.county,
                        suggestion.address?.state,
                        suggestion.address?.postcode,
                      ]
                        .filter(Boolean)
                        .join(', ')}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      
      {/* Show message when no suggestions but user has typed something */}
      {showSuggestions && suggestions.length === 0 && !isLoading && inputValue.trim().length >= 2 && (
        <div className="autocomplete-suggestions autocomplete-no-results">
          <div className="suggestion-item" style={{ cursor: 'default', opacity: 0.7 }}>
            <div className="suggestion-icon"><Lightbulb size={16} /></div>
            <div className="suggestion-text">
              <div className="suggestion-main">No suggestions found</div>
              <div className="suggestion-details">Press Enter to save this address anyway</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddressAutocomplete;
